import {Observable, race} from 'rxjs';

import {FrontWindow, globalWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import {TaskMetadata} from '../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {fromHiddenCallback} from './fromHiddenCallback';

/**
 * Returns an observable that emits once during an animation frame.
 *
 * Warning: animation frames don't fire while the window is hidden or obscured. Make sure you target the right window!
 */
export function fromAnimationFrame(window: FrontWindow, metadata: TaskMetadata): Observable<number> {
  return new Observable((subscriber) => {
    let wasCalled = false;

    const handle = window.requestInstrumentedAnimationFrame((time: number) => {
      wasCalled = true;

      try {
        subscriber.next(time);
        subscriber.complete();
      } catch (error) {
        subscriber.error(error);
      }
    }, metadata);

    return () => {
      if (wasCalled) {
        return;
      }

      window.cancelAnimationFrame(handle);
    };
  });
}

/** Returns an observable that emits once during an animation frame or when the document is hidden. */
export function fromAnimationFrameOrHiddenCallback(
  metadata: TaskMetadata,
  window = globalWindow,
): Observable<number | undefined> {
  return race(fromAnimationFrame(window, metadata), fromHiddenCallback(window));
}
